import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { analytics } from 'core/analytics';
import { InputGroup, InputRightElement, Input, Switch } from '@chakra-ui/react';
import { VoxelDialog } from '@wikifactory/voxel-ui';

import DocumentHead from 'legacyComponents/documentHead';
import { SocialStats } from 'legacyComponents/layout/socialStats';

import { Link } from 'core/routing';

import FormActionButton from 'libs/forms/views/formActionButton';
import { TextField } from 'libs/forms/views/fieldTypes/text';
import { TextAreaField } from 'libs/forms/views/fieldTypes/textArea';

import { clientStore } from 'apps/client/app';
import { scrollHash } from 'apps/utils';
import { Follow } from 'apps/content/views/follow';

import { AvatarInput } from 'apps/components/AvatarInput';
import { ExtendFreeTrialButton } from 'apps/components/ExtendFreeTrialButton';
import Button from 'components/Button';
import Tooltip from 'components/Tooltip';
import { Paragraph } from 'components';
import { Center } from 'legacyComponents/styled/lib';

import { FeatureSwithers } from 'apps/components';

import {
  EditButton,
  ProfileAvatar,
  NameInput,
  DescriptionInput,
  Summary,
  SettingsButtons,
  Fullname,
  Username,
  Bio,
  ProfileInfo,
} from 'apps/profile/views/overview.styles';

@withNamespaces()
@observer
export class ProfileOverview extends React.Component {
  componentDidMount = () => {
    scrollHash();
  };

  render() {
    const { profileManager } = this.props;
    const { profile } = profileManager;
    const { user, fullName, bio } = profile;
    const { canUpdate, editing, editFormStore } = profile;

    const { seats, discount } = profileManager;

    let disableUpdatePromotion = true;

    if (typeof seats === 'number' || typeof discount === 'number') {
      disableUpdatePromotion = false;
    }

    return (
      <>
        <DocumentHead
          ogType='profile'
          ogTypeParams={{
            [`first_name`]: fullName,
            [`username`]: user.username,
          }}
        />
        <ProfileInfo>
          {!editing ? (
            <Summary>
              <ProfileAvatar
                src={profile.avatar?.url}
                name={user.username.substring(1)}
              />
              <Fullname>{fullName}</Fullname>
              <Username>{user.username}</Username>
              <Bio>{bio}</Bio>
            </Summary>
          ) : (
            <Summary>
              <AvatarInput
                fieldStore={editFormStore?.fieldStores.get('avatar')}
              />
              <TextField
                fieldStore={
                  editFormStore && editFormStore.fieldStores.get('fullName')
                }
                editComponent={NameInput}
                validationPosition={{ top: '5px', right: '-25px' }}
              />
              <Username>{user.username}</Username>
              <TextAreaField
                fieldStore={
                  editFormStore && editFormStore.fieldStores.get('bio')
                }
                editComponent={DescriptionInput}
                validationPosition={{ top: '2px', right: '-25px' }}
                className='lightBorder'
              />
            </Summary>
          )}
          <SocialStats app={profile} />
          {!canUpdate && (
            <SettingsButtons>
              <Follow content={profile.content} size='small' />
            </SettingsButtons>
          )}
          {canUpdate && !editing && (
            <SettingsButtons>
              <Tooltip id='tooltipSettings' place='right' delayShow={1000} />
              <EditButton
                outline
                data-testid='PersonalSettingPage-editButton'
                text={<Trans>Edit</Trans>}
                onClick={profile.Edit}
              />
              <Link
                onClick={() => {
                  analytics.sendEvent(
                    'clicked-settings-profile',
                    {},
                    { ga: true },
                  );
                }}
                name='workspace.spaceSettings.account'
                params={{ spacePath: profile.username }}
              >
                <Button
                  icon='settings'
                  ariaLabel='Personal settings'
                  data-tip='Personal settings'
                  data-for='tooltipSettings'
                  outline
                />
              </Link>
            </SettingsButtons>
          )}
          {clientStore.isStaff && (
            <>
              <div className=' flex-center flex-wrap gap-4'>
                {profile.managementData.banned_reason && (
                  <Tooltip
                    id='tooltipStaffSettings'
                    place='top'
                    delayShow={1000}
                  />
                )}
                <Button
                  icon='lightning'
                  outline={!profile.managementData.banned}
                  variant='warning'
                  onClick={
                    profile.managementData.banned
                      ? profileManager.UnBan
                      : profileManager.Ban
                  }
                  data-tip={profile.managementData.banned_reason}
                  data-for='tooltipStaffSettings'
                />
                <Button
                  icon='delete'
                  variant='destructive'
                  onClick={() => {
                    profileManager.SetDeleteProfileDialogVisible(true);
                  }}
                />
                <Button
                  size='small'
                  variant='warning'
                  text='Promotion'
                  icon='settings'
                  onClick={() => {
                    profileManager.QueryBillingSubscription();
                    profileManager.SetBillingDialogVisible(true);
                  }}
                />

                <FeatureSwithers
                  featureFlags={profileManager.featureFlags}
                  onUpdateFeatureFlags={profileManager.UpdateFeatureFlags}
                />

                <ExtendFreeTrialButton
                  extendFreeTrial={profileManager.ExtendFreeTrial}
                  loading={profileManager.freeTrialExtending}
                />
              </div>

              <VoxelDialog
                maxWidth='sm'
                style={{
                  // Promise level more than subheader component zIndex.
                  zIndex: 1000000,
                }}
                fullWidth={true}
                onClose={() => {
                  profileManager.SetDeleteProfileDialogVisible(false);
                }}
                onBackdropClick={() => {
                  profileManager.SetDeleteProfileDialogVisible(false);
                }}
                disableEscapeKeyDown
                open={profileManager.deleteProfileDialogVisible}
                actions={
                  <div className='mt-2'>
                    <Button
                      size='small'
                      variant='primary'
                      outline
                      text='Confirm'
                      disabled={
                        profileManager.deleteProfileVerification !==
                        user.username
                      }
                      onClick={() => {
                        profileManager.Delete();
                      }}
                    />
                  </div>
                }
              >
                <div className='flex items-center justify-center w-full'>
                  <div className='flex flex-col items-start'>
                    <Paragraph>
                      <Center>
                        <Trans>
                          Are you sure you want to delete this user?
                        </Trans>
                      </Center>
                      <Center>
                        <strong>{user.username}</strong>
                      </Center>
                      <Center>
                        <Trans>
                          Type out the username to enable the confirm button:
                        </Trans>
                      </Center>
                      <Center>
                        <InputGroup>
                          <Input
                            type='text'
                            placeholder=''
                            onChange={e => {
                              // - input for delete profile verification, we set the entered string in the profileManager
                              // and only allow deletion if profileManager.deleteProfileVerification === user.username
                              // - also, profile.username can be wrong (just a single '@')! so make sure to use user.username
                              profileManager.SetDeleteProfileVerification(
                                e.target.value,
                              );
                            }}
                          />
                        </InputGroup>
                      </Center>
                    </Paragraph>
                  </div>
                </div>
              </VoxelDialog>

              <VoxelDialog
                maxWidth='sm'
                style={{
                  // Promise level more than subheader component zIndex.
                  zIndex: 1000000,
                }}
                fullWidth={true}
                onClose={() => {
                  profileManager.SetBillingDialogVisible(false);
                }}
                onBackdropClick={() => {
                  profileManager.SetBillingDialogVisible(false);
                }}
                disableEscapeKeyDown
                open={profileManager.billingDialogVisible}
                actions={
                  <div className='mt-2'>
                    <Button
                      size='small'
                      variant='primary'
                      outline
                      text='Update'
                      disabled={disableUpdatePromotion}
                      loading={profileManager.updatingBilling}
                      onClick={() => {
                        profileManager.SubmitPromotionData();
                      }}
                    />
                  </div>
                }
              >
                <div className='flex items-center justify-center w-full'>
                  <div className='flex flex-col items-start'>
                    <span>You can only change free seats or discount</span>
                    <span>
                      current free seats: {profileManager.freeSeatsPreview}
                    </span>
                    <span>
                      current discount: {profileManager.discountPreview}%{' '}
                    </span>

                    <span>is early access: {profileManager.earlyAccess}</span>
                    <div className='flex items-center mt-4'>
                      <span className='inline-block w-32'>Free Seats: </span>
                      <InputGroup className='ml-2'>
                        <Input
                          type='number'
                          width={40}
                          placeholder='0'
                          disabled={profileManager.discountPreview > 0}
                          value={profileManager.seats ?? ''}
                          onChange={e => {
                            profileManager.SetSeats(e.target.value);
                          }}
                        />
                      </InputGroup>
                    </div>
                    <div className='flex items-center mt-2'>
                      <span>Discount:</span>
                      <InputGroup className='ml-8'>
                        <Input
                          placeholder='0-100'
                          width={40}
                          type='number'
                          value={profileManager.discount}
                          disabled={profileManager.freeSeatsPreview > 0}
                          onChange={e => {
                            profileManager.SetDiscount(e.target.value);
                          }}
                        />
                        <InputRightElement>
                          <span>%</span>
                        </InputRightElement>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </VoxelDialog>
            </>
          )}

          {canUpdate && editing && (
            <SettingsButtons>
              <FormActionButton
                formStore={profile.editFormStore}
                buttonHandler={profileManager.Cancel}
                buttonText='Cancel'
                variant='secondary'
                loading={false}
                active={false}
                outline
              />
              <FormActionButton
                outline
                formStore={profile.editFormStore}
                buttonHandler={profileManager.Save}
                buttonText='Save'
                buttonTextPosting='Saving...'
              />
            </SettingsButtons>
          )}
        </ProfileInfo>
      </>
    );
  }
}

ProfileOverview.propTypes = {
  profileManager: PropTypes.object,
  editing: PropTypes.bool,
};
